import React, { useState, useEffect } from 'react';
import { 
  X, Search, Save, Plus, Edit2, Trash2, RotateCcw, 
  DollarSign, User, Calculator, TrendingUp, Receipt, Download
} from 'lucide-react';
import '../styles/CustomerInvoice.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const CustomerReceipts = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Search state
  const [receiptSearch, setReceiptSearch] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  
  // Receipt list
  const [receipts, setReceipts] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  
  // Receipt form state
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [receiptForm, setReceiptForm] = useState(getEmptyReceiptForm());
  
  // Receipt allocations (linking receipt to invoices)
  const [allocations, setAllocations] = useState([]);
  const [editingAllocationIndex, setEditingAllocationIndex] = useState(null);
  const [allocationForm, setAllocationForm] = useState(getEmptyAllocationForm());
  
  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  function getEmptyReceiptForm() {
    return {
      ReceiptNo: '',
      ReceiptDate: new Date().toISOString().slice(0, 10),
      CustomerID: '',
      PaymentModeID: '',
      BankAccountID: '',
      Amount: 0,
      ReferenceNo: '',
      ChequeNo: '',
      ChequeDate: '',
      Notes: '',
      Status: 'Received'
    };
  }
  
  function getEmptyAllocationForm() {
    return {
      InvoiceID: '',
      InvoiceNo: '',
      InvoiceAmount: 0,
      BalanceAmount: 0,
      AllocatedAmount: 0
    };
  }
  
  useEffect(() => {
    loadDropdownData();
    generateReceiptNumber();
    loadReceipts();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onClose) {
          onClose();
        } else {
          navigate('/dashboard');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, navigate]);

  useEffect(() => {
    if (receiptForm.CustomerID) {
      loadUnpaidInvoices(receiptForm.CustomerID);
    } else {
      setUnpaidInvoices([]);
    }
  }, [receiptForm.CustomerID]);
  
  const loadDropdownData = async () => {
    try {
      const [custRes, pmRes, baRes] = await Promise.all([
        apiClient.get('/Customers.php'),
        apiClient.get('/PaymentModes.php'),
        apiClient.get('/BankAccounts.php')
      ]);
      
      if (custRes.data.success) setCustomers(custRes.data.data || []);
      if (pmRes.data.success) setPaymentModes(pmRes.data.data || []);
      if (baRes.data.success) setBankAccounts(baRes.data.data || []);
      
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Error loading dropdown data');
    }
  };
  
  const loadUnpaidInvoices = async (customerId) => {
    try {
      const response = await apiClient.get(`/CustomerReceipts.php?unpaid-invoices=${customerId}`);
      if (response.data.success) {
        setUnpaidInvoices(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading unpaid invoices:', err);
    }
  };
  
  const generateReceiptNumber = async () => {
    try {
      const response = await apiClient.get('/CustomerReceipts.php/next-number');
      if (response.data.success) {
        setReceiptForm(prev => ({ 
          ...prev, 
          ReceiptNo: response.data.data.receiptNo 
        }));
      }
    } catch (err) {
      console.error('Error generating receipt number:', err);
      const timestamp = Date.now();
      setReceiptForm(prev => ({ 
        ...prev, 
        ReceiptNo: `RCP-${timestamp}` 
      }));
    }
  };
  
  const loadReceipts = async () => {
    try {
      const response = await apiClient.get('/CustomerReceipts.php');
      if (response.data.success) {
        setReceipts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading receipts:', err);
    }
  };
  
  const handleReceiptFormChange = (e) => {
    const { name, value } = e.target;
    setReceiptForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleAllocationFormChange = (e) => {
    const { name, value } = e.target;
    setAllocationForm(prev => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'InvoiceID') {
        const selectedInvoice = unpaidInvoices.find(inv => inv.InvoiceID === value);
        if (selectedInvoice) {
          updated.InvoiceNo = selectedInvoice.InvoiceNo;
          updated.InvoiceAmount = parseFloat(selectedInvoice.TotalAmount || 0);
          updated.BalanceAmount = parseFloat(selectedInvoice.BalanceAmount || 0);
          updated.AllocatedAmount = updated.BalanceAmount;
        }
      }
      
      return updated;
    });
  };
  
  const handleAddAllocation = () => {
    if (!allocationForm.InvoiceID) {
      setError('Please select an invoice');
      return;
    }
    
    if (parseFloat(allocationForm.AllocatedAmount) <= 0) {
      setError('Allocated amount must be greater than 0');
      return;
    }
    
    if (parseFloat(allocationForm.AllocatedAmount) > parseFloat(allocationForm.BalanceAmount)) {
      setError('Allocated amount cannot exceed invoice balance');
      return;
    }
    
    // Check if invoice already allocated
    const existingIndex = allocations.findIndex(
      (a, idx) => a.InvoiceID === allocationForm.InvoiceID && idx !== editingAllocationIndex
    );
    
    if (existingIndex !== -1) {
      setError('This invoice is already allocated');
      return;
    }
    
    if (editingAllocationIndex !== null) {
      const updatedAllocations = [...allocations];
      updatedAllocations[editingAllocationIndex] = { ...allocationForm };
      setAllocations(updatedAllocations);
      setEditingAllocationIndex(null);
    } else {
      setAllocations([...allocations, { ...allocationForm }]);
    }
    
    setAllocationForm(getEmptyAllocationForm());
    calculateTotalAllocated([...allocations, allocationForm]);
  };
  
  const handleEditAllocation = (index) => {
    setAllocationForm(allocations[index]);
    setEditingAllocationIndex(index);
  };
  
  const handleDeleteAllocation = (index) => {
    const updatedAllocations = allocations.filter((_, i) => i !== index);
    setAllocations(updatedAllocations);
    calculateTotalAllocated(updatedAllocations);
  };
  
  const handleCancelAllocationEdit = () => {
    setAllocationForm(getEmptyAllocationForm());
    setEditingAllocationIndex(null);
  };
  
  const calculateTotalAllocated = (allocs) => {
    const total = allocs.reduce((sum, alloc) => sum + parseFloat(alloc.AllocatedAmount || 0), 0);
    setReceiptForm(prev => ({ ...prev, Amount: total.toFixed(2) }));
  };
  
  const handleSaveReceipt = async () => {
    if (!receiptForm.CustomerID) {
      setError('Please select a customer');
      return;
    }
    
    if (!receiptForm.PaymentModeID) {
      setError('Please select a payment mode');
      return;
    }
    
    if (parseFloat(receiptForm.Amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const dataToSave = {
        ...receiptForm,
        Allocations: allocations
      };
      
      let response;
      if (editingReceiptId) {
        dataToSave.ReceiptID = editingReceiptId;
        response = await apiClient.put('/CustomerReceipts.php', dataToSave);
      } else {
        response = await apiClient.post('/CustomerReceipts.php', dataToSave);
      }
      
      if (response.data.success) {
        setSuccess(editingReceiptId ? 'Receipt updated successfully!' : 'Receipt created successfully!');
        handleClearAll();
        loadReceipts();
        generateReceiptNumber();
      } else {
        setError(response.data.message || 'Failed to save receipt');
      }
    } catch (err) {
      console.error('Error saving receipt:', err);
      setError('Error saving receipt: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchReceipt = async () => {
    if (!receiptSearch.trim()) {
      setError('Please enter receipt number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get(`/CustomerReceipts.php?receiptno=${receiptSearch}`);
      
      if (response.data.success && response.data.data) {
        loadReceiptForEdit(response.data.data);
      } else {
        setError('Receipt not found');
      }
    } catch (err) {
      console.error('Error searching receipt:', err);
      setError('Error searching receipt: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const loadReceiptForEdit = (receipt) => {
    setEditingReceiptId(receipt.ReceiptID);
    setReceiptForm({
      ReceiptNo: receipt.ReceiptNo,
      ReceiptDate: receipt.ReceiptDate,
      CustomerID: receipt.CustomerID,
      PaymentModeID: receipt.PaymentModeID,
      BankAccountID: receipt.BankAccountID || '',
      Amount: receipt.Amount,
      ReferenceNo: receipt.ReferenceNo || '',
      ChequeNo: receipt.ChequeNo || '',
      ChequeDate: receipt.ChequeDate || '',
      Notes: receipt.Notes || '',
      Status: receipt.Status
    });
    setAllocations(receipt.Allocations || []);
    setSelectedReceipt(receipt);
  };
  
  const handleClearAll = () => {
    setReceiptForm(getEmptyReceiptForm());
    setAllocations([]);
    setAllocationForm(getEmptyAllocationForm());
    setEditingReceiptId(null);
    setEditingAllocationIndex(null);
    setSelectedReceipt(null);
    setReceiptSearch('');
    setError('');
    setSuccess('');
    generateReceiptNumber();
  };
  
  if (!session?.companyid) {
    return (
      <div 
        className="invoice-form-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (onClose) {
              onClose();
            } else {
              navigate('/dashboard');
            }
          }
        }}
      >
        <div className="invoice-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button 
              className="close-btn-fixed" 
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  navigate('/dashboard');
                }
              }}
              type="button"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="invoice-form-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (onClose) {
            onClose();
          } else {
            navigate('/dashboard');
          }
        }
      }}
    >
      <div className="invoice-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header">
          <div className="header-left">
            <Receipt size={24} />
            <h2>Customer Receipt (Payment Received)</h2>
          </div>
          <button 
            className="close-btn-fixed"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="invoice-toolbar">
          <button onClick={handleSearchReceipt} disabled={loading} className="btn-toolbar">
            <Search size={18} />
            Search
          </button>
          <button 
            onClick={handleSaveReceipt} 
            disabled={loading || parseFloat(receiptForm.Amount) <= 0} 
            className="btn-toolbar btn-save"
          >
            <Save size={18} />
            {editingReceiptId ? 'Update' : 'Save'}
          </button>
          <button onClick={handleClearAll} disabled={loading} className="btn-toolbar">
            <RotateCcw size={18} />
            Clear All
          </button>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <strong>Success:</strong> {success}
          </div>
        )}
        
        {/* Search Section */}
        <div className="invoice-search-section">
          <label>Receipt No:</label>
          <input
            type="text"
            value={receiptSearch}
            onChange={(e) => setReceiptSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchReceipt()}
            placeholder="Enter receipt number to search"
            disabled={loading}
          />
          <button onClick={handleSearchReceipt} disabled={loading} className="btn-search-inline">
            <Search size={18} />
          </button>
        </div>
        
        <div className="form-content">
          {/* Receipt Header */}
          <div className="form-section">
            <h3 className="section-title">
              <DollarSign size={18} />
              Receipt Details
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Receipt No *</label>
                <input
                  type="text"
                  name="ReceiptNo"
                  value={receiptForm.ReceiptNo}
                  onChange={handleReceiptFormChange}
                  readOnly
                  style={{ background: '#e3f2fd', fontWeight: 'bold' }}
                />
              </div>
              
              <div className="form-group">
                <label>Receipt Date *</label>
                <input
                  type="date"
                  name="ReceiptDate"
                  value={receiptForm.ReceiptDate}
                  onChange={handleReceiptFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Customer *</label>
                <select
                  name="CustomerID"
                  value={receiptForm.CustomerID}
                  onChange={handleReceiptFormChange}
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(c => (
                    <option key={c.customerid} value={c.customerid}>
                      {c.customername} ({c.customercode})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Payment Mode *</label>
                <select
                  name="PaymentModeID"
                  value={receiptForm.PaymentModeID}
                  onChange={handleReceiptFormChange}
                  required
                >
                  <option value="">Select Payment Mode</option>
                  {paymentModes.map(pm => (
                    <option key={pm.paymentmodeid} value={pm.paymentmodeid}>
                      {pm.PaymentMode}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Bank Account</label>
                <select
                  name="BankAccountID"
                  value={receiptForm.BankAccountID}
                  onChange={handleReceiptFormChange}
                >
                  <option value="">Select Bank Account</option>
                  {bankAccounts.map(ba => (
                    <option key={ba.bankaccountid} value={ba.bankaccountid}>
                      {ba.BankName} - {ba.AccountNumber}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  name="Amount"
                  value={receiptForm.Amount}
                  onChange={handleReceiptFormChange}
                  placeholder="0.00"
                  style={{ background: '#fff3cd', fontWeight: 'bold', fontSize: '16px' }}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Reference No</label>
                <input
                  type="text"
                  name="ReferenceNo"
                  value={receiptForm.ReferenceNo}
                  onChange={handleReceiptFormChange}
                  placeholder="Transaction reference"
                />
              </div>
              
              <div className="form-group">
                <label>Cheque No</label>
                <input
                  type="text"
                  name="ChequeNo"
                  value={receiptForm.ChequeNo}
                  onChange={handleReceiptFormChange}
                  placeholder="If payment by cheque"
                />
              </div>
              
              <div className="form-group">
                <label>Cheque Date</label>
                <input
                  type="date"
                  name="ChequeDate"
                  value={receiptForm.ChequeDate}
                  onChange={handleReceiptFormChange}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="Status"
                  value={receiptForm.Status}
                  onChange={handleReceiptFormChange}
                >
                  <option value="Received">Received</option>
                  <option value="Cleared">Cleared</option>
                  <option value="Bounced">Bounced</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Invoice Allocations */}
          {receiptForm.CustomerID && (
            <div className="form-section">
              <h3 className="section-title">
                <Calculator size={18} />
                Allocate to Invoices (Optional)
              </h3>
              
              {/* Add/Edit Allocation Form */}
              <div className="item-form">
                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                  <div className="form-group">
                    <label>Invoice</label>
                    <select
                      name="InvoiceID"
                      value={allocationForm.InvoiceID}
                      onChange={handleAllocationFormChange}
                    >
                      <option value="">Select Invoice</option>
                      {unpaidInvoices.map(inv => (
                        <option key={inv.InvoiceID} value={inv.InvoiceID}>
                          {inv.InvoiceNo} - Balance: {parseFloat(inv.BalanceAmount).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Invoice Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={allocationForm.InvoiceAmount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Balance Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={allocationForm.BalanceAmount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Allocated Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="AllocatedAmount"
                      value={allocationForm.AllocatedAmount}
                      onChange={handleAllocationFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={handleAddAllocation} className="btn-primary btn-sm" style={{ width: '100%' }}>
                      {editingAllocationIndex !== null ? (
                        <>
                          <Save size={16} />
                          Update
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Add
                        </>
                      )}
                    </button>
                    {editingAllocationIndex !== null && (
                      <button onClick={handleCancelAllocationEdit} className="btn-secondary btn-sm" style={{ marginLeft: '8px' }}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Allocations List */}
              {allocations.length > 0 && (
                <div className="invoice-items-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice No</th>
                        <th className="text-right">Invoice Amount</th>
                        <th className="text-right">Balance</th>
                        <th className="text-right">Allocated</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((alloc, index) => (
                        <tr key={index}>
                          <td>{alloc.InvoiceNo}</td>
                          <td className="text-right">{parseFloat(alloc.InvoiceAmount).toFixed(2)}</td>
                          <td className="text-right">{parseFloat(alloc.BalanceAmount).toFixed(2)}</td>
                          <td className="text-right font-weight-bold">
                            {parseFloat(alloc.AllocatedAmount).toFixed(2)}
                          </td>
                          <td className="actions-cell">
                            <button
                              onClick={() => handleEditAllocation(index)}
                              className="btn-icon btn-edit"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteAllocation(index)}
                              className="btn-icon btn-delete"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f9f9f9', fontWeight: 'bold' }}>
                        <td colSpan="3" style={{ textAlign: 'right', paddingRight: '12px' }}>
                          Total Allocated:
                        </td>
                        <td className="text-right" style={{ color: '#667eea', fontSize: '16px' }}>
                          {allocations.reduce((sum, a) => sum + parseFloat(a.AllocatedAmount || 0), 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {/* Notes */}
          <div className="form-section">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="Notes"
                  value={receiptForm.Notes}
                  onChange={handleReceiptFormChange}
                  rows="3"
                  placeholder="Additional notes about this receipt"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReceipts;